/**
 * Os anexos de UMA pergunta.
 *
 * Ancorado na pergunta, nunca num balde da trilha: foi o campo genérico de anexos que fez o B2B
 * perder a referência de qual arquivo era de qual pergunta — política comercial, mockup e imagem
 * de filtros chegavam juntos, sem identificação. Aqui cada arquivo nasce ao lado da pergunta que
 * o pediu, e é assim que a regra do "em anexo" sem anexo passa a ser verificável.
 *
 * Quem envia é um **botão**, e não o campo de arquivo cru do navegador. "Escolher arquivo /
 * Nenhum arquivo escolhido" é texto do sistema operacional: não diz o que a pergunta está
 * pedindo, não se parece com nenhum outro controle do formulário e, no meio de trinta perguntas,
 * some. O campo continua no DOM — é ele que abre o seletor —, só que fora da tela.
 */

import { useRef } from "react";
import { Botao } from "../ui";
import {
  formatarTamanho,
  formatosAceitos,
  urlDoAnexo,
  type PerguntaDoFormulario,
} from "../../nucleo/kickoff";

export interface PropsDosAnexos {
  pergunta: PerguntaDoFormulario;
  desabilitado: boolean;
  podeRemover?: boolean;
  ocupado: boolean;
  /** A recusa do último envio desta pergunta — aparece aqui, ao lado do botão que a causou. */
  falha?: string | undefined;
  aoAnexar: (arquivo: File) => void;
  aoRemover: (anexoId: string) => void;
}

export function AnexosDaPergunta({
  pergunta,
  desabilitado,
  podeRemover = !desabilitado,
  ocupado,
  falha,
  aoAnexar,
  aoRemover,
}: PropsDosAnexos) {
  const entrada = useRef<HTMLInputElement>(null);
  const { formatos, tamanho_maximo_mb: teto } = pergunta.config;

  return (
    <div className="anexos" data-testid={`anexos-${pergunta.id}`}>
      <p className="anexos__titulo">Arquivos desta pergunta</p>

      {pergunta.anexos.length === 0 ? (
        <p className="anexos__vazio">Nenhum arquivo enviado ainda.</p>
      ) : (
        <ul className="anexos__lista">
          {pergunta.anexos.map((anexo) => (
            <li className="anexos__item" key={anexo.id}>
              {/* O download sai da API como binário, sempre — o `content-type` gravado veio do
                  navegador de quem enviou e não é prova de nada. */}
              <a className="anexos__nome" href={urlDoAnexo(anexo.id)}>
                {anexo.nome_original}
              </a>
              <span className="anexos__tamanho numero-tabular">
                {formatarTamanho(anexo.tamanho)}
              </span>
              {!podeRemover ? null : (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  disabled={ocupado}
                  onClick={() => aoRemover(anexo.id)}
                >
                  Remover
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}

      {desabilitado ? null : (
        <>
          <input
            ref={entrada}
            type="file"
            className="anexos__entrada"
            tabIndex={-1}
            aria-hidden="true"
            accept={formatosAceitos(pergunta.config)}
            disabled={ocupado}
            onChange={(evento) => {
              const arquivo = evento.target.files?.[0];
              // O campo é limpo na hora: sem isso, escolher o mesmo arquivo de novo depois de
              // uma falha não dispara `change` e a pessoa clica sem nada acontecer.
              evento.target.value = "";
              if (arquivo) aoAnexar(arquivo);
            }}
          />
          {/* O rótulo acessível carrega o enunciado: num formulário com várias perguntas de
              anexo, trinta botões dizendo "Selecionar anexo" não distinguem uma da outra para
              quem ouve a tela. */}
          <Botao
            tamanho="pequeno"
            disabled={ocupado}
            aria-label={`Selecionar anexo para "${pergunta.enunciado}"`}
            data-testid={`selecionar-anexo-${pergunta.id}`}
            onClick={() => entrada.current?.click()}
          >
            {ocupado ? "Enviando…" : "Selecionar anexo"}
          </Botao>
          {falha ? (
            <p className="anexos__falha" role="alert" data-testid={`falha-do-anexo-${pergunta.id}`}>
              {falha}
            </p>
          ) : null}
          <p className="anexos__regra">
            {formatos && formatos.length > 0
              ? `Formatos aceitos: ${formatos.join(", ")}.`
              : "Formatos usuais de documento e imagem."}
            {teto ? ` Até ${teto} MB por arquivo.` : null}
          </p>
        </>
      )}
    </div>
  );
}
